import {getDomainGraphqlError, toRelayId} from 'graphqlsupport';
import {useAsyncState} from 'placement/hook/useAsyncState';
import {useToggleMatchOffer} from 'placement/hook/useToggleMatchOffer';
import {useGoToReferralListPage} from 'placement/routes';
import {Offer, OfferStatus, Referral} from 'placement/domains';
import React, {useCallback, useState} from 'react';
import {BehaviorSubject} from 'rxjs';
import {SlaConfirmationDialog} from './offer-detail/SlaConfirmationDialog';
import {find} from 'lodash';
import {PAGE, REFERRAL_STATUS} from "../const";
import {isOfferDoesntIncludeClinic} from "./provider-offer-detail-right-rail/common";
import {useDeclineOffer} from "../hook/useDeclineOffer";
import {DeclineOfferConfirmationDialog} from "./offer-detail/DeclineOfferConfirmationDialog";
import { showToast } from 'common';

type MarkSelectedMatchModalProp = {
    selectedItem: Offer | null;
    selectedProviderNotMatchSiblingProvider?: boolean;
    setUnmatchReturnModalState: (show: 'Unmatch' | 'hide') => void;
    setSelectedItem: (offer: Offer | null) => void;
    referrals: Referral[];
    recentPage: PAGE;
    isOtherReferralsHasOffer?: boolean
    refetchReferrals: () => void;
}


export function MarkSelectedMatchModal({
                                           selectedItem,
                                           setSelectedItem,
                                           referrals,
                                           recentPage,
                                           selectedProviderNotMatchSiblingProvider = false,
                                           isOtherReferralsHasOffer = false,
                                           refetchReferrals,
                                           setUnmatchReturnModalState,
                                       }: MarkSelectedMatchModalProp) {
    const [loading, setLoading] = useAsyncState<boolean>(false);
    const {execute} = useToggleMatchOffer();
    const {execute: decline} = useDeclineOffer();
    let isSelected = false;
    const referral = find(referrals, (r) => r.guid === selectedItem?.referralGuid);

    if (referral && referral.matchedOffer) {
        isSelected = selectedItem?.guid === referral.matchedOffer.guid
    }

    const shouldDisabled = selectedItem?.status === OfferStatus.DECLINED ||
        (referral?.status === REFERRAL_STATUS.MATCHED && selectedItem?.guid !== referral?.matchedOffer?.guid) ||
        recentPage === PAGE.COMPLETE_REFERRALS;

    const shouldDisableDecline = referral?.status === REFERRAL_STATUS.MATCHED || recentPage === PAGE.COMPLETE_REFERRALS;

    const mustProceedUnmatchByModal = referral?.status === REFERRAL_STATUS.MATCHED
        && selectedItem?.guid === referral?.matchedOffer?.guid

    let loadingTextCss = '';
    let confirmText = isSelected ? 'unmatch' : 'match';
    if (loading) {
        loadingTextCss = 'loading-text';
        confirmText = 'Loading';
    }

    const goToReferralListPage = useGoToReferralListPage();

    const backToReferralList = React.useCallback(
        () => {
            goToReferralListPage();
        },
        [goToReferralListPage]
    );

    const [showSlaConfirmation$] = useState(() => new BehaviorSubject<boolean>(false));
    const [showDeclineConfirmation$] = useState(() => new BehaviorSubject<boolean>(false));

    const toggleMatchOffer = useCallback(async (offer: Offer) => {
        setLoading(true);
        let haveError = false;
        try {
            await execute({variables: {offerGuid: offer.guid, isSelected: !isSelected}});
        } catch (e) {
            console.log(e);
            haveError = true;
            let errMessage = getDomainGraphqlError(e)
            if (!errMessage) {
                errMessage = 'Something went wrong when trying to toggle match for offer';
            }
            showToast.error(errMessage);
        }
        if (!haveError) {
            showToast.success(`Success ${confirmText} with selected offer`);
        }
        setLoading(false);

        if (!isSelected && isOtherReferralsHasOffer) {
            backToReferralList();
            return;
        }

        showSlaConfirmation$.next(false);

        await refetchReferrals();
    }, [backToReferralList, confirmText, execute, isSelected, setLoading, isOtherReferralsHasOffer, refetchReferrals, showSlaConfirmation$]);


    const declineOffer = useCallback(async (offer: Offer) => {
        setLoading(true);
        let haveError = false;
        try {
            await decline({variables: {offerId:  toRelayId(offer.guid, 'Offer')}});
        } catch (e) {
            console.log(e);
            haveError = true;
            let errMessage = getDomainGraphqlError(e)
            if (!errMessage) {
                errMessage = 'Something went wrong when trying to decline offer';
            }
            showToast.error(errMessage);
        }
        if (!haveError) {
            showToast.success(`Success decline selected offer`);
        }
        setLoading(false);
        if (!isSelected && isOtherReferralsHasOffer) {
            backToReferralList();
            return;
        }

        showDeclineConfirmation$.next(false);
        setSelectedItem(null);

        await refetchReferrals();
    }, [setLoading, showDeclineConfirmation$, setSelectedItem, refetchReferrals, decline]);

    const offerDoesntIncludeClinic = isOfferDoesntIncludeClinic(selectedItem, referral);

    const onMatched = useCallback((offer: Offer) => {
        if (isSelected || (offer.satisfiedSla && !selectedProviderNotMatchSiblingProvider && !offerDoesntIncludeClinic)) {
            toggleMatchOffer(offer);
        } else {
            showSlaConfirmation$.next(true);
        }
    }, [isSelected, showSlaConfirmation$, toggleMatchOffer, selectedProviderNotMatchSiblingProvider, offerDoesntIncludeClinic]);

    return (
        <React.Fragment>
            {
                selectedItem !== null && (<>
                    <div className='absolute bg-black rounded-l placement-provider-offer-select-panel-position'>
                        <div className='px-10 py-5 flex justify-between'>
                            <button
                                className='bg-black border border-white text-white py-2 px-4 rounded w-120px'
                                onClick={() => setSelectedItem(null)}>
                                Cancel
                            </button>
                            {

                                <button
                                    className={`bg-red-500 hover:bg-red-600 text-white py-2 px-4 ${loadingTextCss} w-120px rounded
                                     ${shouldDisableDecline || loading ? 'opacity-50 cursor-not-allowed' : ''} capitalize`}
                                    disabled={shouldDisableDecline || loading}
                                    onClick={() => showDeclineConfirmation$.next(true)}
                                >
                                    Decline
                                </button>
                            }
                            <button
                                className={`text-white py-2 px-4 ${loadingTextCss}  w-120px rounded ${shouldDisabled || loading ? 'opacity-50 cursor-not-allowed' : ''} 
                                        ${mustProceedUnmatchByModal ? 'bg-red-500 hover:bg-red-600' : 'bg-atlantis-200 hover:bg-atlantis-300'}
                                        capitalize`}
                                disabled={shouldDisabled || loading}
                                onClick={() => mustProceedUnmatchByModal ? setUnmatchReturnModalState('Unmatch') : onMatched(selectedItem)}
                            >
                                {confirmText}
                            </button>
                        </div>
                    </div>
                    <DeclineOfferConfirmationDialog show$={showDeclineConfirmation$}
                                                    onConfirmed={() => declineOffer(selectedItem)}
                                                    loading={loading}/>
                    <SlaConfirmationDialog
                        show$={showSlaConfirmation$}
                        messages={
                            [
                                !selectedItem.satisfiedSla ? "does not meet SLA" : '',
                                selectedProviderNotMatchSiblingProvider ? 'have different provider for sibling referrals' : '',
                                offerDoesntIncludeClinic ? "does not provide services in a clinic" : '',
                            ]
                        }
                        onConfirmed={() => toggleMatchOffer(selectedItem)}
                    />
                </>)
            }
        </React.Fragment>
    );
}
